"use client";

import Image from "next/image";
import FilterOutlinedLogo from "/public/_a/icons/programListing/filter.svg";
import DownLtLogo from "~/icons/down-lt.svg";
import CloseIcon from "~/icons/close.svg";
import { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import cn from "clsx";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { searchPrograms } from "@/components/molecules/ProgramMenu/constants";

function getCategory(item=""){
  if(!item) return "NA";
  switch (item) {
    case "domain":
      return "Domain";
    case "duration":
      return "Duration";
    case "trendingType":  
      return "Trending Type";
    default:
      return "Program Type";
  }
}

export default function ProgramFiltersMenu({
  programs,
  toggleFilters,
  setFilteredPrograms,
  setIsFiltersApplied,
  isProgramSpecific = false,
  setPreviousPrograms,
  query,
  setQuery
}) {
  const [appliedFilters, setAppliedFilters] = useState({
    programType: [],
    domain: [],
    duration: [],
    trendingType: [],
  });
  const [appliedFiltersList, setAppliedFiltersList] = useState([]);
  const [filtersList, setFiltersList] = useState({
    programType: [],
    domain: [],
    duration: [],
    trendingType: [],
  });
  
  const createListOfFilters = useCallback(() => {
    const filters = {
      programType: new Set(),
      domain: new Set(),
      duration: new Set(),
      trendingType: new Set(),
    };
    programs?.map((item) => {
      if (item?.programType) {
        filters?.programType?.add(item?.programType);
      }

      if (item?.domain) {
        filters?.domain?.add(item?.domain);
      }

      if (item?.duration) {
        filters?.duration?.add(item?.duration?.toLowerCase());
      }

      if (item?.trendingType) {
        filters?.trendingType?.add(item?.trendingType);
      }
    });
    const programTypeArray = Array.from(filters?.programType);
    const orderedProgramTypeArray = programTypeArray.filter(pt => pt !== 'UG_Program' && pt !== 'PG_Program' && pt!=='Certification');
    if (filters?.programType?.has('UG_Program')) {
      orderedProgramTypeArray.unshift('UG_Program');
    }
    if (filters?.programType?.has('PG_Program')) {
      orderedProgramTypeArray.splice(1, 0, 'PG_Program');
    }
    if (filters?.programType?.has('Certification')) {
      orderedProgramTypeArray.splice(2, 0, "Certification")
    }
    setFiltersList({
      programType: orderedProgramTypeArray,
      domain: Array.from(filters?.domain),
      duration: Array.from(filters?.duration),
      trendingType: Array.from(filters?.trendingType),
    });
  }, [programs, filtersList, setFiltersList]);

  const addFilter = useCallback(
    (type, value) => {
      if (appliedFilters[type].includes(value)) return;
      setAppliedFilters((prevFilters) => {
        const newAppliedFilters = {
          ...prevFilters,
          [type]: [...prevFilters[type], value],
        };
        return newAppliedFilters;
      });
      
      const updatedFiltersList = [...appliedFiltersList, { type, value }];
      
      setAppliedFiltersList(updatedFiltersList);
      const ctaValues = updatedFiltersList.map(item => item.value.replace(/_/g, " ")).join('|');
      const categories = Array.from(new Set(updatedFiltersList.map(item => getCategory(item.type)))).join('|');
      

      pushToDataLayer({
        event: "filter_section_interaction",
        cta_text: value,
        action: "add",
        category: categories,
        option_selected: ctaValues,
        section_name: "Explore our online degree programs",
        page_type: getGlobalStateValue('pageType'),
      });
    },
    [appliedFilters, appliedFiltersList]
  );

  const removeFilter = useCallback(
    (type, value) => {
      setAppliedFilters((prevFilters) => {
        const newAppliedFilters = { ...prevFilters };
        newAppliedFilters[type] = newAppliedFilters[type].filter(
          (item) => item !== value
        );
        return {
          ...newAppliedFilters,
        };
      });
      const updatedFiltersList = appliedFiltersList.filter((item) => item.value !== value);
      const ctaValues = updatedFiltersList.map(item => item.value.replace(/_/g, " ")).join('|');
      // const categories = Array.from(new Set(updatedFiltersList.map(item => getCategory(item.type)))).join('|');
      setAppliedFiltersList(updatedFiltersList);
      
      pushToDataLayer({
        event: "filter_section_interaction",
        cta_text: value?.replace(/_/g, " "),
        action: "remove",
        category: getCategory(type || ""),
        option_selected: ctaValues || "NA",
        section_name: "Explore our online degree programs",
        page_type: getGlobalStateValue('pageType'),
      })

    },
    [appliedFilters, appliedFiltersList]
  );

  const handleClearAll = () => {
    const ctaValues = appliedFiltersList.map(item => item.value.replace(/_/g, " ")).join('|');
    const categories = Array.from(new Set(appliedFiltersList.map(item => getCategory(item.type)))).join('|');

    pushToDataLayer({
      event: "filter_section_interaction",
      cta_text: "Clear all",
      action: "remove",
      category: categories || "NA",
      option_selected: ctaValues || "NA",
      section_name: "Explore our online degree programs",
      page_type: getGlobalStateValue('pageType'),
    });
    setAppliedFilters({
      programType: [],
      domain: [],
      duration: [],
      trendingType: [],
    });
    setAppliedFiltersList([]);
    setPreviousPrograms(programs);
  }

  useEffect(() => {
    createListOfFilters();
  }, []);

  function searchPrograms(query, programs) {
    query = query.toLowerCase(); // Convert query to lowercase for case-insensitive search

    return programs?.filter((program) => {
      const { programName, domain, specializations } = program;

      // Convert all strings to lowercase for comparison
      const lowercaseProgramName = programName?.toLowerCase();
      const lowercaseDomain = domain?.toLowerCase();
      const lowercaseSpecializations = specializations.map((spec) =>
        spec.toLowerCase()
      );

      // Check for partial matches in program_name, domain, or specializations
      return (
        lowercaseProgramName?.includes(query) ||
        lowercaseDomain?.includes(query) ||
        lowercaseSpecializations?.some((spec) => spec?.includes(query))
      );
    });
  }
  useEffect(() => {
   
    const filteredPrograms = programs?.filter((item) => {
      return Object.entries(appliedFilters).every(([key, value]) => {
        const itemValue = item[key] ? item[key].toLowerCase() : '';
        const filterValue = value.map(v => v.toLowerCase());
        return value.length === 0 || filterValue.includes(itemValue);
      });
    });

    setIsFiltersApplied(appliedFiltersList.length > 0);

    
    setPreviousPrograms(filteredPrograms);
    if(query) {
      setFilteredPrograms(searchPrograms(query, filteredPrograms));
    }
    else {
      setFilteredPrograms(filteredPrograms);

    }
  }, [appliedFilters]);

  
  return (
    <>
     <div className={styles.filterContainer}>
      <div className="flex items-center relative mb-2">
        <Image
          src={FilterOutlinedLogo}
          alt="filters"
          className={styles.filterLogo}
        />
        <p className={styles.filterTitle}>Filters</p>
        <Image
          src={DownLtLogo}
          alt="back"
          className={styles.backBtn}
          onClick={toggleFilters}
        />
      </div>
      {appliedFiltersList.length > 0 ? (
        <div className={styles.filterType}>
          <div className="flex items-center justify-between">
            <p>Applied filters</p>
            <p
              className={styles.clear}
              onClick={() => {
                handleClearAll()
                
              }}
            >
              Clear all
            </p>
          </div>
          <div className={styles.btnGroup}>
            {appliedFiltersList?.map((item: any, i) => (
              <button key={i} className={styles.highlightedBtn}>
                <span>{item?.value?.replace(/_/g, " ")}</span>
                <Image
                  onClick={() => {
                    removeFilter(item?.type, item?.value);
                  }}
                  src={CloseIcon}
                  alt={"Remove"}
                  width={10}
                  height={10}
                />
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {filtersList?.programType.length > 0 ? (
        <div className={styles.filterType}>
          <p>Program Type</p>
          <div className={styles.btnGroup}>
            {filtersList?.programType?.map((item, i) => (
              <button
                onClick={() =>{addFilter("programType", item)}}
                key={i}
                className={cn({
                  [styles.highlightedBtn]:
                    isProgramSpecific || appliedFilters?.programType?.includes(item),
                })}
                disabled={isProgramSpecific}
              >
                {item?.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {filtersList?.domain?.length > 0 ? (
        <div className={styles.filterType}>
          <p>Domain</p>
          <div className={styles.btnGroup}>
            {filtersList?.domain?.map((item, i) => (
              <button
                onClick={() => addFilter("domain", item)}
                key={i}
                className={cn({
                  [styles.highlightedBtn]: appliedFilters?.domain?.includes(item),
                })}
              >
                {item?.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div> )
      : null }
      {filtersList?.duration?.length > 0 ? ( 
        <div className={styles.filterType}>
          <p>Duration</p>
          <div className={styles.btnGroup}>
            {filtersList?.duration?.map((item, i) => (
              <button
                onClick={() => addFilter("duration", item)}
                key={i}
                className={cn({
                  [styles.highlightedBtn]:
                    appliedFilters?.duration?.includes(item),
                })}
              >
                {item?.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {filtersList?.trendingType?.length > 0 ? (
        <div className={styles.filterType}>
          <p>Trending Programs</p>
          <div className={styles.btnGroup}>
            {filtersList?.trendingType?.map((item, i) => (
              <button
                onClick={() => addFilter("trendingType", item)}
                key={i}
                className={cn({
                  [styles.highlightedBtn]:
                    appliedFilters?.trendingType?.includes(item),
                })}
              >
                {item?.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      </div>
    </>
  );
}
